import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Bar, Radar, PolarArea } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { GoalsContext } from '../State/GoalsContext';
import { TargetsContext } from '../State/TargetsContext';
import { IndicatorsContext } from '../State/IndicatorsContext';
import { GeneralDataContext } from '../State/GeneralDataContext';
import { normalizeGoalType, goalTypeToUrl } from '../utils/goalTypeUtils';
import { extractGoalNumberFromEuCode, normalizeGoalNumber } from '../utils/indicatorGoalMatch';
import { route } from '../utils/apiRoutes';
import { transformGoalPreview, transformTargetPreview } from '../utils/transformApiResponse';
import { sortGoals, sortBySdgIndex } from '../utils/sortUtils';
import { getGoalDisplayTitle, stripBrokenGoalHeadingPrefix } from '../utils/goalDisplayTitle';
import SDGSpinner from './SDGSpinner';
import TrendArrow from './TrendArrow';
import StatusSemaphore from './StatusSemaphore';
import {
  euStatusCategoryToCode,
  EU_STATUS_LABELS,
  EU_STATUS_COLORS,
  isEuStatusUnavailable,
} from '../utils/euStatusUtils';
import {
  trendCategoryToChartCode,
  EU_TREND_CHART_LABELS,
  getTrendLegendLabels,
  getTrendArrow,
} from '../utils/euTrendUtils';
import { stripLeadingIndicatorCode } from '../utils/displayText';
import './SDGProfilePage.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

// SDG Colors - Official UN SDG color palette
const SDG_COLORS = {
  '1': '#E5243B', '2': '#DDA63A', '3': '#4C9F38', '4': '#C5192D', '5': '#FF3A21',
  '6': '#26BDE2', '7': '#FCC30B', '8': '#A21942', '9': '#FD6925', '10': '#DD1367',
  '11': '#FD9D24', '12': '#BF8B2E', '13': '#3F7E44', '14': '#0A97D9', '15': '#56C02B',
  '16': '#00689D', '17': '#19486A'
};

// Status colors
const STATUS_COLORS = {
  0: '#bdbdbd', // Information unavailable / Not applicable
  1: '#d3302f', // Very far from target / Major challenges
  2: '#f67c01', // Far from target / Significant challenges
  3: '#fcc30c', // Moderate progress / Challenges remain
  4: '#43a047', // Close to target / Achieved / Good progress
};

// Map status category strings to numeric codes (0-4) for chart aggregation
const statusCategoryToCode = (category) => {
  if (!category) return 0;
  const cat = String(category).toLowerCase();
  
  // Status categories from API (Eurostat method)
  if (cat.includes('close') || cat.includes('achieved')) return 4;
  if (cat.includes('good')) return 4;
  if (cat.includes('moderate')) return 3;
  if (cat.includes('far') && !cat.includes('very')) return 2;
  if (cat.includes('very far')) return 1;
  if (cat.includes('not applicable') || cat.includes('unavailable')) return 0;
  
  // If it's already a number, return it
  const num = parseInt(category, 10);
  if (!isNaN(num) && num >= 0 && num <= 4) return num;
  
  return 0;
};

// Map trend category strings to numeric codes (0-4) for chart aggregation (UN only; EU uses euTrendUtils)
const trendCategoryToCode = (category) => trendCategoryToChartCode(category, 'un');

const STATUS_LABELS = {
  0: 'Unavailable',
  1: 'Not Applicable',
  2: 'Far',
  3: 'Moderate',
  4: 'Close/Achieved',
};

const TREND_LABELS = {
  0: 'No Data',
  1: 'Decreasing / Regression',
  2: 'Stagnating',
  3: 'Moderately Improving',
  4: 'On Track / Significant progress',
};

const TREND_DISTRIBUTION_COLUMNS = [
  { code: 4, unLabel: 'On Track', euLabel: EU_TREND_CHART_LABELS[4] },
  { code: 3, unLabel: 'Moderately Improving', euLabel: EU_TREND_CHART_LABELS[3] },
  { code: 2, unLabel: 'Stagnating', euLabel: EU_TREND_CHART_LABELS[2] },
  { code: 1, unLabel: 'Decreasing', euLabel: EU_TREND_CHART_LABELS[1] },
  { code: 0, unLabel: 'No Data', euLabel: EU_TREND_CHART_LABELS[0] },
];

const goalIdKey = (id) => String(id);

const targetBelongsToGoal = (target, goalId) => {
  const gid = Number(goalId);
  if (!Number.isFinite(gid) || gid <= 0) return false;
  const goalIds = target.goal_ids ?? target.acf?.select_goal;
  const ids = Array.isArray(goalIds) ? goalIds : goalIds != null ? [goalIds] : [];
  return ids.some((id) => Number(id) === gid);
};

const isInsufficientTrend = (category) => {
  if (category == null || String(category).trim() === '') return true;
  const cat = String(category).toLowerCase();
  return cat.includes('insufficient') || cat.includes('no data') || cat.includes('not applicable');
};

/** Goal-level assessment from transformed preview (same shape as home grid / goal detail). */
const goalAssessmentFromPreview = (goal) => ({
  statusCategory: goal?.computed?.goal_semaphore_status ?? null,
  trendCategory: goal?.computed?.goal_trend_status ?? null,
  statusScore: goal?.computed?.goal_status_score ?? null,
  trendScore: goal?.computed?.goal_trend_score ?? null,
});

/**
 * Same rule as IndicatorsCatalogPage clickable rows: complete setup and not draft.
 * Denominator is the full catalogue (include_incomplete), not published-only previews.
 */
const catalogIndicatorHasData = (ind) =>
  ind?.setup_complete === true && ind?.post_status !== 'draft';

const countCatalogCoverage = (list) => {
  const items = Array.isArray(list) ? list : [];
  return {
    total: items.length,
    withData: items.filter(catalogIndicatorHasData).length,
  };
};

const SDGProfilePage = () => {
  const {
    goals: contextGoals,
    goalType: contextGoalType,
    loading: goalsLoading,
    setGoalType,
    fetchGoals,
  } = useContext(GoalsContext);
  const {
    targets: contextTargets,
    loading: targetsLoading,
    fetchAllTargetsByType,
  } = useContext(TargetsContext);
  const { indicators, fetchAllIndicatorsByType, loading: indicatorsLoading } = useContext(IndicatorsContext);
  const { data: generalData } = useContext(GeneralDataContext);
  const { goalType: urlGoalType } = useParams();
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileDataType, setProfileDataType] = useState(null);
  const [previewGoals, setPreviewGoals] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [catalogIndicators, setCatalogIndicators] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Get goalType from URL, fallback to context, default to 'un'
  const goalType = normalizeGoalType(urlGoalType || contextGoalType || 'un');
  
  // Update context goalType if URL has a different one
  useEffect(() => {
    if (urlGoalType && goalType !== contextGoalType) {
      setGoalType(goalType);
    } else if (!urlGoalType && goalType !== contextGoalType) {
      // If no URL goalType but we have a different goalType, update context
      setGoalType(goalType);
    }
  }, [urlGoalType, goalType, contextGoalType, setGoalType]);

  // Redirect if accessing /sdg-profile without goalType
  useEffect(() => {
    if (!urlGoalType) {
      const urlPath = goalTypeToUrl(goalType);
      navigate(`/${urlPath}/sdg-profile`, { replace: true });
    }
  }, [urlGoalType, goalType, navigate]);

  // Determine if EU or UN
  const isEU = goalType === 'eu';
  const mapStatusCode = (category) =>
    isEU ? euStatusCategoryToCode(category) : statusCategoryToCode(category);
  const mapTrendCode = (category) => trendCategoryToChartCode(category, goalType);
  const statusLabelsForCharts = isEU ? EU_STATUS_LABELS : STATUS_LABELS;
  const statusColorsForCharts = isEU ? EU_STATUS_COLORS : STATUS_COLORS;
  const statusLevelsForCharts = isEU ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4];

  // Get general data values
  const indexScoreValueRaw = isEU 
    ? (generalData?.eu_sdg_index_score_value || '')
    : (generalData?.global_sdg_index_score_value || '');
  const indexScoreValue = indexScoreValueRaw ? Number(indexScoreValueRaw).toFixed(2) : '';
  
  const indexRankValueRaw = isEU 
    ? (generalData?.eu_sdg_index_rank_value)
    : (generalData?.global_sdg_index_rank_value);
  const indexRankValue = Array.isArray(indexRankValueRaw) ? (indexRankValueRaw[0] ?? '') : indexRankValueRaw;
  const peerScores = isEU ? (generalData?.eu_peer_scores) : (generalData?.global_peer_scores);
  const indexRankOutOf = Array.isArray(peerScores) ? peerScores.length : '';
  const indexRankDisplay = indexRankValue && indexRankOutOf ? `${indexRankValue}/${indexRankOutOf}` : '';

  // Profile page: goals/preview (same source as home + goal detail) + profile-data (targets bundle).
  useEffect(() => {
    if (!urlGoalType || !goalType) return;

    let cancelled = false;
    setProfileLoading(true);
    setProfileData(null);
    setProfileDataType(null);
    setPreviewGoals(null);
    setProfileError('');

    if (goalType !== contextGoalType) {
      setGoalType(goalType);
    }

    const requestedType = goalType;
    const profileUrl = route.profileData(requestedType, false);
    const goalsUrl = route.goalsPreviewBatch(requestedType, false);

    Promise.all([
      fetch(profileUrl).then((response) => {
        if (!response.ok) throw new Error(`profile-data ${response.status}`);
        return response.json();
      }).catch((error) => {
        console.error('Error fetching profile targets:', error);
        return null;
      }),
      fetch(goalsUrl).then((response) => {
        if (!response.ok) throw new Error(`goals/preview ${response.status}`);
        return response.json();
      }).catch((error) => {
        console.error('Error fetching profile goals:', error);
        return null;
      }),
    ])
      .then(([profile, goalsBatch]) => {
        if (cancelled) return;

        if (profile) {
          setProfileData(profile);
          setProfileDataType(requestedType);
        } else {
          fetchAllTargetsByType(requestedType);
        }

        if (goalsBatch) {
          const goalsArray = Array.isArray(goalsBatch) ? goalsBatch : (goalsBatch?.goals || []);
          setPreviewGoals(sortGoals(goalsArray.map((g) => transformGoalPreview(g))));
        } else {
          fetchGoals(requestedType);
        }

        if (!profile || !goalsBatch) {
          setProfileError('Some profile data could not be loaded from WordPress.');
        }

        setProfileLoading(false);
        fetchAllIndicatorsByType(requestedType);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching profile data:', error);
        setProfileLoading(false);
        setProfileData(null);
        setProfileDataType(null);
        setGoalType(requestedType);
        fetchGoals(requestedType);
        fetchAllTargetsByType(requestedType);
        fetchAllIndicatorsByType(requestedType);
      });

    return () => {
      cancelled = true;
    };
  }, [goalType, urlGoalType, contextGoalType, setGoalType, fetchGoals, fetchAllTargetsByType, fetchAllIndicatorsByType]);

  // Full catalogue (published + incomplete/draft) — same source as IndicatorsCatalogPage.
  useEffect(() => {
    if (!goalType) return;

    let cancelled = false;
    setCatalogLoading(true);
    setCatalogIndicators([]);

    fetch(route.indicatorsListWithIncomplete(goalType))
      .then((response) => {
        if (!response.ok) throw new Error(`indicators catalogue ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setCatalogIndicators(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error('Error fetching indicators catalogue for profile:', error);
        if (!cancelled) setCatalogIndicators([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [goalType]);

  // Sort goals by SDG index using the unified sort (same logic used for
  // indicators and targets across the app). Prefer goals from profile data
  // when present (already filtered by goalType), otherwise fall back to
  // context goals.
  // Canonical goal assessments: goals/preview batch (identical to home grid).
  const sortedGoals = useMemo(() => {
    if (previewGoals && previewGoals.length > 0) {
      return previewGoals;
    }
    if (profileDataType === goalType && profileData?.goals?.length) {
      return sortGoals(profileData.goals.map((g) => transformGoalPreview(g)));
    }
    if (contextGoalType === goalType && contextGoals.length > 0) {
      return sortGoals(contextGoals);
    }
    return [];
  }, [previewGoals, profileData, profileDataType, goalType, contextGoalType, contextGoals]);

  const profileTargets = useMemo(() => {
    if (profileDataType !== goalType) {
      return contextGoalType === goalType ? contextTargets : [];
    }
    if (!profileData?.targets || !Array.isArray(profileData.targets)) {
      return contextGoalType === goalType ? contextTargets : [];
    }
    return profileData.targets.map((t) => transformTargetPreview(t));
  }, [profileData, profileDataType, goalType, contextGoalType, contextTargets]);

  // Goal-level stats from goals/preview (must match home grid).
  const stats = useMemo(() => {
    if (!sortedGoals.length) return null;

    const statusCounts = isEU
      ? { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      : { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    const trendCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };

    const goalStatusData = sortedGoals.map((goal) => {
      const { statusCategory, trendCategory } = goalAssessmentFromPreview(goal);
      const statusCode = mapStatusCode(statusCategory);
      const trendCode = mapTrendCode(trendCategory);
      statusCounts[statusCode] = (statusCounts[statusCode] || 0) + 1;
      trendCounts[trendCode] = (trendCounts[trendCode] || 0) + 1;
      return { statusCode, trendCode, statusCategory, trendCategory };
    });

    const hasTrend = goalStatusData.filter((g) => !isInsufficientTrend(g.trendCategory)).length;
    const hasStatus = isEU
      ? goalStatusData.filter(
          (g) => g.statusCategory && !isEuStatusUnavailable(g.statusCategory)
        ).length
      : goalStatusData.filter((g) => g.statusCategory && mapStatusCode(g.statusCategory) > 0).length;

    return {
      statusCounts,
      trendCounts,
      goalStatusData,
      totalGoals: sortedGoals.length,
      missingStatus: statusCounts[0] || 0,
      missingTrend: trendCounts[0] || 0,
      hasStatus,
      hasTrend,
    };
  }, [sortedGoals, isEU, mapStatusCode, mapTrendCode]);

  // Status comparison chart - Kosovo vs EU27 quintile band per goal (EU only)
  const statusChartData = useMemo(() => {
    if (!isEU || !stats || !sortedGoals.length) return null;

    const labels = sortedGoals.map(goal => {
      const index = goal.acf?.index || '';
      return `Goal ${index}`;
    });

    const datasets = statusLevelsForCharts.map(statusLevel => ({
      label: statusLabelsForCharts[statusLevel],
      data: sortedGoals.map((goal, idx) => {
        const goalStatus = stats.goalStatusData[idx]?.statusCode ?? 0;
        return goalStatus === statusLevel ? 1 : 0;
      }),
      backgroundColor: statusColorsForCharts[statusLevel] + '80',
      borderColor: statusColorsForCharts[statusLevel],
      borderWidth: 1,
    }));

    return { labels, datasets };
  }, [sortedGoals, stats, isEU, statusLevelsForCharts, statusLabelsForCharts, statusColorsForCharts]);

  // Trend comparison chart - showing trend distribution per goal
  const trendChartData = useMemo(() => {
    if (!stats || !sortedGoals.length || sortedGoals.length === 0) return null;

    const labels = sortedGoals.map(goal => {
      const index = goal.acf?.index || '';
      return `Goal ${index}`;
    });

    // Create datasets for each trend level (0-4) - show which goal has which trend
    const datasets = [0, 1, 2, 3, 4].map(trendLevel => ({
      label: isEU ? EU_TREND_CHART_LABELS[trendLevel] : TREND_LABELS[trendLevel],
      data: sortedGoals.map((goal, idx) => {
        const goalTrend = stats.goalStatusData[idx].trendCode;
        return goalTrend === trendLevel ? 1 : 0;
      }),
      backgroundColor: STATUS_COLORS[trendLevel] + '80', // 50% opacity
      borderColor: STATUS_COLORS[trendLevel],
      borderWidth: 1,
    }));

    return {
      labels,
      datasets,
    };
  }, [sortedGoals, stats, isEU]);

  // Overall status distribution chart
  const overallStatusChartData = useMemo(() => {
    if (!isEU || !stats) return null;

    return {
      labels: statusLevelsForCharts.map((k) => statusLabelsForCharts[k]),
      datasets: [{
        label: 'Number of Goals',
        data: statusLevelsForCharts.map((k) => stats.statusCounts[k] || 0),
        backgroundColor: statusLevelsForCharts.map((k) => statusColorsForCharts[k]),
        borderColor: statusLevelsForCharts.map((k) => statusColorsForCharts[k]),
        borderWidth: 2,
      }],
    };
  }, [stats, isEU, statusLevelsForCharts, statusLabelsForCharts, statusColorsForCharts]);

  // Overall trend distribution chart
  const overallTrendChartData = useMemo(() => {
    if (!stats) return null;

    const trendLabels = isEU ? EU_TREND_CHART_LABELS : TREND_LABELS;

    return {
      labels: Object.keys(trendLabels).map((k) => trendLabels[k]),
      datasets: [{
        label: 'Number of Goals',
        data: Object.keys(trendLabels).map((k) => stats.trendCounts[k] || 0),
        backgroundColor: Object.keys(trendLabels).map((k) => STATUS_COLORS[k]),
        borderColor: Object.keys(trendLabels).map((k) => STATUS_COLORS[k]),
        borderWidth: 2,
      }],
    };
  }, [stats, isEU]);

  // Target Status Distribution by Goal - Simple column data
  const targetStatusByGoal = useMemo(() => {
    if (!stats || !sortedGoals.length) return [];

    // Use targets from profile data if available, otherwise from context
    const targetsToUse = profileTargets;
    
    if (!targetsToUse || targetsToUse.length === 0) return [];

    return sortedGoals.map(goal => {
      const goalId = goal.id;
      const index = goal.acf?.index || '';
      const counts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
      
      const goalTargets = targetsToUse.filter((target) => targetBelongsToGoal(target, goalId));
      
      // Count statuses
      goalTargets.forEach(target => {
        // Try multiple paths to get status (check both computed and assessment structures)
        const statusCategory = target.computed?.target_semaphore_status || 
                               target.assessment?.status?.category ||
                               target.acf?.target_semaphore_status ||
                               null;
        const statusCode = mapStatusCode(statusCategory);
        counts[statusCode] = (counts[statusCode] || 0) + 1;
      });
      
      const total = statusLevelsForCharts.reduce((sum, lvl) => sum + (counts[lvl] || 0), 0);
      return {
        goalLabel: `Goal ${index}`,
        goalId: goal.id,
        counts,
        total,
      };
    });
  }, [sortedGoals, stats, profileTargets, isEU, statusLevelsForCharts, mapStatusCode]);

  // Target Progress Distribution by Goal - Simple column data
  const targetTrendByGoal = useMemo(() => {
    if (!stats || !sortedGoals.length) return [];

    // Use targets from profile data if available, otherwise from context
    const targetsToUse = profileTargets;
    
    if (!targetsToUse || targetsToUse.length === 0) return [];

    return sortedGoals.map(goal => {
      const goalId = goal.id;
      const index = goal.acf?.index || '';
      const counts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
      
      const goalTargets = targetsToUse.filter((target) => targetBelongsToGoal(target, goalId));
      
      // Count trends
      goalTargets.forEach(target => {
        // Try multiple paths to get trend (check both computed and assessment structures)
        const trendCategory = target.computed?.target_trend_status || 
                              target.assessment?.trend?.category ||
                              target.acf?.target_trend_status ||
                              null;
        const trendCode = mapTrendCode(trendCategory);
        counts[trendCode] = (counts[trendCode] || 0) + 1;
      });
      
      return {
        goalLabel: `Goal ${index}`,
        goalId: goal.id,
        counts,
        total: counts[0] + counts[1] + counts[2] + counts[3] + counts[4]
      };
    });
  }, [sortedGoals, stats, profileTargets, isEU, statusLevelsForCharts, mapStatusCode, mapTrendCode, goalType]);


  // Prepare goal data for individual display - use calculated values from filtered targets
  const goalsData = useMemo(() => {
    return sortedGoals.map((goal) => {
      const index = goal.acf?.index || '';
      const { statusCategory, trendCategory, statusScore, trendScore } = goalAssessmentFromPreview(goal);

      return {
        id: goal.id,
        index,
        title: getGoalDisplayTitle(goal),
        color: SDG_COLORS[index] || '#039dda',
        statusCategory,
        trendCategory,
        statusScore,
        trendScore,
        goalIndexScore: statusScore,
      };
    });
  }, [sortedGoals]);

  // Download page as PDF with proper page breaks
  const handleDownloadPDF = async () => {
    if (!pageRef.current || isGeneratingPDF) return;
    
    setIsGeneratingPDF(true);
    
    try {
      // Hide the download button temporarily
      const downloadButton = document.querySelector('.download-section-compact');
      if (downloadButton) {
        downloadButton.style.display = 'none';
      }

      const container = pageRef.current;

      // Capture the entire page as one image
      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        windowWidth: container.scrollWidth,
        windowHeight: container.scrollHeight,
        allowTaint: true,
      });

      // Show the button again
      if (downloadButton) {
        downloadButton.style.display = 'block';
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Page margins
      const pageMargin = 10; // mm
      const usableWidth = pdfWidth - (pageMargin * 2);
      const usableHeight = pdfHeight - (pageMargin * 2);
      
      // Convert canvas dimensions to mm
      // html2canvas with scale 2: canvas is 2x larger in pixels
      // 96 DPI = 0.264583 mm per pixel (at original scale)
      const scale = 2;
      const mmPerPixel = 0.264583;
      const logicalWidth = canvas.width / scale;
      const logicalHeight = canvas.height / scale;
      
      const imgWidth = logicalWidth * mmPerPixel;
      const imgHeight = logicalHeight * mmPerPixel;
      
      // Scale to fit page width
      const widthRatio = usableWidth / imgWidth;
      const scaledWidth = usableWidth;
      const scaledHeight = imgHeight * widthRatio;
      
      // Find protected elements that shouldn't be split (in mm coordinates)
      const containerRect = container.getBoundingClientRect();
      const protectedElements = [];
      
      const addProtectedElement = (element) => {
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;
        const relativeHeight = rect.height;
        
        // Convert to mm (accounting for scale and width ratio)
        const top = (relativeTop * mmPerPixel) * widthRatio;
        const height = (relativeHeight * mmPerPixel) * widthRatio;
        protectedElements.push({ top, bottom: top + height, height });
      };
      
      // Get all elements that shouldn't be split
      const charts = container.querySelectorAll('.chart-card-white');
      const tables = container.querySelectorAll('.distribution-table-wrapper, table');
      const goalCards = container.querySelectorAll('.goal-card-white');
      const goalsGrid = container.querySelector('.goals-grid');
      const goalsLegend = container.querySelector('.goals-legend');
      const indicatorsSection = container.querySelector('.indicators-performance-section');
      
      charts.forEach(addProtectedElement);
      tables.forEach(addProtectedElement);
      goalCards.forEach(addProtectedElement);
      addProtectedElement(goalsGrid);
      addProtectedElement(goalsLegend);
      addProtectedElement(indicatorsSection);
      
      protectedElements.sort((a, b) => a.top - b.top);
      
      // Function to check if a split point would cut through a protected element
      const wouldCutElement = (splitY) => {
        return protectedElements.some(el => {
          const margin = 5; // 5mm margin
          return splitY > (el.top + margin) && splitY < (el.bottom - margin);
        });
      };
      
      // Split content into pages
      let currentY = 0;
      let pageNum = 0;
      
      while (currentY < scaledHeight) {
        if (pageNum > 0) {
          pdf.addPage();
        }
        
        let pageEndY = Math.min(currentY + usableHeight, scaledHeight);
        
        // Check if we would cut through a protected element
        if (wouldCutElement(pageEndY)) {
          // Find the next protected element that would be cut
          const currentYValue = currentY; // Capture for use in callbacks
          const pageEndYValue = pageEndY; // Capture for use in callbacks
          
          const cutElement = protectedElements.find(el => 
            pageEndYValue > el.top && pageEndYValue < el.bottom
          );
          
          if (cutElement) {
            // Try to fit the element on this page
            if (cutElement.bottom <= currentYValue + usableHeight) {
              pageEndY = cutElement.bottom;
            } else {
              // Element doesn't fit, start new page for it
              pageEndY = cutElement.top;
            }
          } else {
            // Find next element that starts in this range
            const nextElement = protectedElements.find(el => 
              el.top > currentYValue && el.top < pageEndYValue
            );
            if (nextElement && nextElement.bottom <= currentYValue + usableHeight) {
              pageEndY = nextElement.bottom;
            } else if (nextElement) {
              pageEndY = nextElement.top;
            }
          }
        }
        
        // Ensure we have content
        if (pageEndY <= currentY) {
          pageEndY = Math.min(currentY + usableHeight, scaledHeight);
        }
        
        // Calculate source coordinates in canvas pixels
        // Convert from mm back to original pixels
        const sourceY = (currentY / widthRatio / mmPerPixel) * scale;
        const sourceHeight = ((pageEndY - currentY) / widthRatio / mmPerPixel) * scale;
        
        // Extract page content
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(sourceHeight);
        const pageCtx = pageCanvas.getContext('2d');
        
        pageCtx.drawImage(
          canvas,
          0, Math.floor(sourceY), canvas.width, Math.ceil(sourceHeight),
          0, 0, canvas.width, Math.ceil(sourceHeight)
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
        const pageHeightInMm = pageEndY - currentY;
        
        pdf.addImage(
          pageImgData,
          'PNG',
          pageMargin,
          pageMargin,
          scaledWidth,
          pageHeightInMm
        );
        
        currentY = pageEndY;
        pageNum++;
        
        // Safety check
        if (pageNum > 100) {
          console.warn('PDF generation stopped: too many pages');
          break;
        }
      }

      pdf.save(`Kosovo_${goalType.toUpperCase()}_SDG_Profile.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Group indicators by goal_id (through targets)
  const indicatorsByGoal = useMemo(() => {
    if (!indicators || indicators.length === 0 || !sortedGoals.length) return {};

    // Use targets from profile data if available, otherwise from context
    const targetsToUse = profileTargets;
    
    if (!targetsToUse || targetsToUse.length === 0) return {};

    // Create a map of target_id -> goal_ids
    const targetToGoalsMap = {};
    targetsToUse.forEach(target => {
      const targetId = target.id;
      let goalIds = [];
      
      // Get goal_ids from target
      if (target.goal_ids && Array.isArray(target.goal_ids)) {
        goalIds = target.goal_ids;
      } else if (target.acf?.select_goal) {
        const selectGoal = target.acf.select_goal;
        goalIds = Array.isArray(selectGoal) ? selectGoal : [selectGoal];
      }
      
      if (goalIds.length > 0) {
        targetToGoalsMap[targetId] = goalIds;
      }
    });

    // Group indicators by goal
    const grouped = {};
    sortedGoals.forEach(goal => {
      grouped[goal.id] = {
        goal,
        indicators: []
      };
    });

    // For each indicator, find its goals through targets
    indicators.forEach(indicator => {
      let indicatorGoalIds = [];
      
      // Try direct goal_id first (if available from preview)
      if (indicator.goal_id) {
        indicatorGoalIds.push(indicator.goal_id);
      }
      
      // Also check through target_id
      if (indicator.target_id) {
        const goalIds = targetToGoalsMap[indicator.target_id];
        if (goalIds && Array.isArray(goalIds)) {
          indicatorGoalIds.push(...goalIds);
        }
      }
      
      // Also check acf.select_targets
      if (indicator.acf?.select_targets && Array.isArray(indicator.acf.select_targets)) {
        indicator.acf.select_targets.forEach(targetId => {
          const goalIds = targetToGoalsMap[targetId];
          if (goalIds && Array.isArray(goalIds)) {
            indicatorGoalIds.push(...goalIds);
          }
        });
      }
      
      // Fallback: infer goal from code (UN: 1.1.1; EU: sdg_11_52)
      if (indicatorGoalIds.length === 0 && indicator.acf?.index) {
        const code = indicator.acf.index;
        let goalNumber = '';
        if (goalType === 'eu') {
          goalNumber = extractGoalNumberFromEuCode(code);
        } else {
          const indexMatch = code.match(/^(\d+)/);
          goalNumber = indexMatch ? normalizeGoalNumber(indexMatch[1]) : '';
        }
        if (goalNumber) {
          const goalByIndex = sortedGoals.find(
            (g) => normalizeGoalNumber(g.acf?.index || '') === goalNumber
          );
          if (goalByIndex) {
            indicatorGoalIds.push(goalByIndex.id);
          }
        }
      }
      
      // Remove duplicates
      indicatorGoalIds = [...new Set(indicatorGoalIds)];
      
      // Add indicator to all its goals (or skip if no goal found)
      if (indicatorGoalIds.length > 0) {
        indicatorGoalIds.forEach(goalId => {
          if (grouped[goalId]) {
            grouped[goalId].indicators.push(indicator);
          }
        });
      }
    });

    return grouped;
  }, [indicators, sortedGoals, profileTargets, goalType]);

  // Catalogue coverage: full list including incomplete/draft (matches IndicatorsCatalogPage).
  const indicatorCoverage = useMemo(
    () => countCatalogCoverage(catalogIndicators),
    [catalogIndicators]
  );

  const catalogByGoalId = useMemo(() => {
    const grouped = {};
    catalogIndicators.forEach((ind) => {
      const gid = ind.goal_id || 0;
      if (!grouped[gid]) grouped[gid] = [];
      grouped[gid].push({
        ...ind,
        // sortBySdgIndex reads index/code; catalogue payload uses indicator_code
        code: ind.indicator_code || ind.code,
        index: ind.indicator_code || ind.index,
      });
    });
    Object.keys(grouped).forEach((gid) => {
      grouped[gid] = sortBySdgIndex(grouped[gid]);
    });
    return grouped;
  }, [catalogIndicators]);

  const hasGoalsReady = sortedGoals.length > 0;
  const hasTargetsReady = profileTargets.length > 0;
  const hasStats = stats !== null && stats !== undefined;

  if (
    profileLoading ||
    (!hasGoalsReady && goalsLoading) ||
    (!hasTargetsReady && targetsLoading)
  ) {
    return <SDGSpinner size="lg" text="Loading SDG Profile..." />;
  }

  if (!hasGoalsReady || !hasTargetsReady || !hasStats) {
    return (
      <div className="sdg-profile-load-error" role="alert">
        {profileError || 'The SDG profile data is not available from WordPress.'}
      </div>
    );
  }

  return (
    <div className="sdg-profile-page" ref={pageRef}>
      <Container fluid className="profile-container">
        {/* Compact Header */}
        <Row className="profile-header-compact">
          <Col>
            <div className="header-content">
              <img 
                src="/sdg-logo/logo-horizontal.png" 
                className="sdg-logo-compact" 
                alt="Kosovo SDG Logo" 
              />
              <div className="header-title-text">
                <h2 className="profile-page-title">
                  {isEU ? 'Kosovo EU SDG Profile' : 'Kosovo UN SDG Profile'}
                </h2>
              </div>
            </div>
          </Col>
        </Row>

        {/* Summary Statistics */}
        <Row className="summary-section">
          <Col md={isEU ? 3 : 4} sm={6} xs={12}>
            <div className="stat-card">
              <div className="stat-value">{stats.totalGoals}</div>
              <div className="stat-label">Total Goals</div>
            </div>
          </Col>
          <Col md={isEU ? 3 : 4} sm={6} xs={12}>
            <div className="stat-card">
              <div className="stat-value">{stats.hasTrend}</div>
              <div className="stat-label">Goals with Progress</div>
            </div>
          </Col>
          {isEU && (
            <Col md={3} sm={6} xs={12}>
              <div className="stat-card">
                <div className="stat-value">{stats.hasStatus}</div>
                <div className="stat-label">Goals with Kosovo status</div>
              </div>
            </Col>
          )}
          <Col md={isEU ? 3 : 4} sm={6} xs={12}>
            <div className="stat-card">
              <div className="stat-value" aria-live="polite">
                {catalogLoading ? (
                  '—'
                ) : (
                  <>
                    <span>{indicatorCoverage.withData}</span>
                    <span className="stat-value-divider"> / </span>
                    <span className="stat-value-total">{indicatorCoverage.total}</span>
                  </>
                )}
              </div>
              <div className="stat-label">Indicators with data</div>
            </div>
          </Col>
        </Row>
        {!catalogLoading && indicatorCoverage.total > 0 && (
          <Row className="summary-coverage-note-row">
            <Col>
              <p className="summary-coverage-note">
                {indicatorCoverage.withData} of {indicatorCoverage.total}{' '}
                {isEU ? 'EU' : 'UN'} SDG indicators currently have published data for Kosovo.
                {indicatorCoverage.total - indicatorCoverage.withData > 0 && (
                  <>
                    {' '}
                    The other {indicatorCoverage.total - indicatorCoverage.withData} are in the
                    catalogue but not yet available (draft or incomplete setup).
                  </>
                )}{' '}
                See the{' '}
                <Link to={`/${goalTypeToUrl(goalType)}/indicators-catalog`}>
                  full indicators catalogue
                </Link>
                .
              </p>
            </Col>
          </Row>
        )}

        {isEU && statusChartData && (
        <Row className="charts-section">
          <Col md={12}>
            <div className="chart-card-white">
              <h3 className="chart-title-compact">Kosovo status comparison by goal</h3>
              <p className="chart-explanation">
                Each bar is one SDG goal. Colour shows Kosovo&apos;s quintile band vs the default comparison group (EU27)
                based on indicator headline values — not distance-to-target.
              </p>
              <div className="bar-chart-wrapper">
                <Bar
                  data={statusChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { position: 'bottom', labels: { padding: 10, font: { size: 10 }, boxWidth: 12 } },
                      tooltip: {
                        callbacks: {
                          label: (context) => (context.parsed.y === 1 ? `${context.dataset.label}: Yes` : ''),
                        },
                      },
                    },
                    scales: {
                      x: { stacked: true, ticks: { font: { size: 9 }, maxRotation: 45, minRotation: 45 } },
                      y: { stacked: true, ticks: { stepSize: 1, font: { size: 10 } }, title: { display: true, text: 'Status band', font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
        )}

        {isEU && overallStatusChartData && (
        <Row className="charts-section">
          <Col md={12}>
            <div className="chart-card-white">
              <h3 className="chart-title-compact">Overall Kosovo status distribution</h3>
              <p className="chart-explanation">
                How many goals fall into each quintile band when Kosovo is compared to EU27 on the headline series.
              </p>
              <div className="bar-chart-wrapper-small">
                <Bar
                  data={overallStatusChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
        )}

        {/* Progress Comparison Chart */}
        <Row className="charts-section">
          <Col md={12}>
            <div className="chart-card-white">
              <h3 className="chart-title-compact">Progress Distribution by Goal</h3>
              <p className="chart-explanation">
                This chart shows the trend distribution across all goals. Each bar represents a goal, 
                with segments showing how many targets fall into each trend category (On Track, Moderately Improving, Stagnating, Decreasing, No Data).
              </p>
              {trendChartData && trendChartData.datasets && trendChartData.datasets.length > 0 ? (
                <div className="bar-chart-wrapper">
                  <Bar
                    data={trendChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            padding: 10,
                            font: {
                              size: 10,
                            },
                            boxWidth: 12,
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              if (context.parsed.y === 1) {
                                return `${context.dataset.label}: Yes`;
                              }
                              return '';
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          stacked: true,
                          ticks: {
                            font: {
                              size: 9,
                            },
                            maxRotation: 45,
                            minRotation: 45,
                          },
                        },
                        y: {
                          stacked: true,
                          ticks: {
                            stepSize: 1,
                            font: {
                              size: 10,
                            },
                          },
                          title: {
                            display: true,
                            text: 'Progress Level',
                            font: {
                              size: 11,
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="bar-chart-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                  <SDGSpinner size="md" text="Loading chart data..." />
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Overall Progress Distribution Chart */}
        <Row className="charts-section">
          <Col md={12}>
            <div className="chart-card-white">
              <h3 className="chart-title-compact">Overall Progress Distribution</h3>
              <p className="chart-explanation">
                This chart provides an overview of the overall trend distribution across all goals. 
                It shows the total count of goals in each trend category, indicating the general direction of progress.
              </p>
              {overallTrendChartData && overallTrendChartData.datasets && overallTrendChartData.datasets.length > 0 ? (
                <div className="bar-chart-wrapper-small">
                  <Bar
                    data={overallTrendChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `${context.parsed.y} goals`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1,
                            font: {
                              size: 10,
                            },
                          },
                          title: {
                            display: true,
                            text: 'Number of Goals',
                            font: {
                              size: 11,
                            },
                          },
                        },
                        x: {
                          ticks: {
                            font: {
                              size: 10,
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="bar-chart-wrapper-small" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '250px' }}>
                  <SDGSpinner size="md" text="Loading chart data..." />
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Target Progress Distribution by Goal */}
        <Row className="charts-section">
          <Col md={12}>
            <div className="chart-card-white">
              <h3 className="chart-title-compact">
                {isEU ? 'Sub-theme Progress Distribution by Goal' : 'Target Progress Distribution by Goal'}
              </h3>
              <p className="chart-explanation">
                {isEU
                  ? 'Shows the count of Eurostat sub-themes for each goal by progress category (EU methodology).'
                  : 'Shows the count of targets for each goal by trend level. Each row represents a goal, with columns showing the count for each trend category.'}
              </p>
              {targetTrendByGoal && targetTrendByGoal.length > 0 ? (
                <div className="distribution-table-wrapper">
                  <table className="distribution-table">
                    <thead>
                      <tr>
                        <th>Goal</th>
                        {TREND_DISTRIBUTION_COLUMNS.map((col) => (
                          <th
                            key={col.code}
                            style={{ backgroundColor: STATUS_COLORS[col.code] + '40' }}
                          >
                            {isEU ? col.euLabel : col.unLabel}
                          </th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targetTrendByGoal.map((goalData, idx) => (
                        <tr key={goalData.goalId || idx}>
                          <td className="goal-label-cell"><strong>{goalData.goalLabel}</strong></td>
                          {TREND_DISTRIBUTION_COLUMNS.map((col) => (
                            <td
                              key={col.code}
                              style={{ backgroundColor: STATUS_COLORS[col.code] + '20', textAlign: 'center' }}
                            >
                              {goalData.counts[col.code]}
                            </td>
                          ))}
                          <td className="total-cell"><strong>{goalData.total}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                  <SDGSpinner size="md" text="Loading chart data..." />
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Individual Goals Section */}
        <Row className="goals-section pdf-page-break-before">
          <Col>
            <h2 className="section-title-compact">All Goals</h2>
            
            {/* Legends for All Goals cards */}
            <div className="goals-legend">
              {isEU && (
                <div className="legend-section">
                  <h5 className="legend-title">Status</h5>
                  <div className="legend-items">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div key={level} className="legend-item">
                        <span
                          className="legend-status-dot"
                          style={{ backgroundColor: EU_STATUS_COLORS[level] }}
                        />
                        <span className="legend-text">{EU_STATUS_LABELS[level]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="legend-section">
                <h5 className="legend-title">Progress</h5>
                <div className="legend-items">
                  {getTrendLegendLabels(goalType).map((label) => {
                    const { arrow, color } = getTrendArrow(label);
                    return (
                      <div key={label} className="legend-item">
                        <span className="legend-arrow" style={{ color }}>
                          {arrow}
                        </span>
                        <span className="legend-text">{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {goalsData && goalsData.length > 0 ? (
              <div className="goals-grid">
                {goalsData.map((goal) => (
                <div
                  key={goal.id}
                  className="goal-card-white"
                  style={{ borderTopColor: goal.color }}
                >
                  <div className="goal-card-header">
                    <div className="goal-number-badge" style={{ backgroundColor: goal.color }}>
                      Goal {goal.index}
                    </div>
                  </div>
                  <h4 className="goal-card-title">{getGoalDisplayTitle(goal)}</h4>
                  <div className="goal-card-status">
                    {isEU && (goal.statusCategory || goal.statusScore != null) && (
                      <div className="goal-status-item">
                        <span className="goal-status-label">Status:</span>
                        <StatusSemaphore
                          category={goal.statusCategory}
                          score={goal.statusScore}
                          size={16}
                          showCaption={false}
                        />
                        <span className="goal-status-text">
                          {goal.statusCategory || 'N/A'}
                        </span>
                      </div>
                    )}
                    {(goal.trendCategory || goal.trendScore != null) && (
                      <div className="goal-status-item">
                        <span className="goal-status-label">Progress:</span>
                        <TrendArrow
                          category={goal.trendCategory}
                          score={goal.trendScore}
                          size={16}
                          showCaption={false}
                        />
                        <span className="goal-status-text">
                          {goal.trendCategory || 'N/A'}
                        </span>
                      </div>
                    )}
                  </div>
                  {goal.goalIndexScore !== null && goal.goalIndexScore !== undefined && (
                    <div className="goal-index-score">
                      Index Score: {goal.goalIndexScore.toFixed(2)}
                    </div>
                  )}
                </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <SDGSpinner size="md" text="Loading goals data..." />
              </div>
            )}
          </Col>
        </Row>

        {/* Performance by Indicator Section */}
        <Row className="indicators-performance-section">
          <Col>
            <div className="chart-card-white">
              <h2 className="section-title-compact">Performance by Indicator</h2>
              <p className="section-coverage-hint">
                Showing indicators with published data only. For the complete list, including
                indicators without data, see the{' '}
                <Link to={`/${goalTypeToUrl(goalType)}/indicators-catalog`}>
                  full indicators catalogue
                </Link>
                .
              </p>
              {indicatorsLoading || catalogLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                  <SDGSpinner size="md" text="Loading indicators..." />
                </div>
              ) : (
                <div className="indicators-by-goal-list">
                  {indicators.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      No indicators with published data yet.{' '}
                      <Link to={`/${goalTypeToUrl(goalType)}/indicators-catalog`}>
                        View the full indicators catalogue
                      </Link>
                      .
                    </div>
                  ) : Object.keys(indicatorsByGoal).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      {indicators.length} indicator(s) found but could not be associated with goals.
                    </div>
                  ) : (
                    sortedGoals.map((goal) => {
                      const catalogForGoal = catalogByGoalId[goal.id] || [];
                      const goalIndicators = indicatorsByGoal[goal.id]?.indicators || [];
                      if (goalIndicators.length === 0) return null;

                      const goalIndex = goal.acf?.index || '';
                      const goalColor = SDG_COLORS[goalIndex] || '#039dda';
                      const goalCoverage = countCatalogCoverage(
                        catalogForGoal.length > 0 ? catalogForGoal : goalIndicators
                      );
                      const coverageLabel = `(${goalCoverage.withData}/${goalCoverage.total} with data)`;

                      return (
                        <div key={goal.id} className="goal-indicators-group">
                          <div className="goal-indicators-header" style={{ borderLeftColor: goalColor }}>
                            <span className="goal-indicators-number" style={{ backgroundColor: goalColor }}>
                              Goal {goalIndex}
                            </span>
                            <span className="goal-indicators-title">
                              {stripBrokenGoalHeadingPrefix(goal.acf?.name || goal.title_render || goal.title || '')}
                            </span>
                            <span className="goal-indicators-count">{coverageLabel}</span>
                          </div>
                          <div className="indicators-list-tight">
                            {goalIndicators.map((indicator) => {
                              const trendData = getTrendArrow(
                                indicator?.computed?.trend_status || indicator?.computed?.trend
                              );
                              const statusCat = indicator?.computed?.semaphore_status;
                              return (
                                <div key={indicator.id} className="indicator-item-tight">
                                  <div className="indicator-status-icons">
                                    {isEU && statusCat && !isEuStatusUnavailable(statusCat) && (
                                      <StatusSemaphore
                                        category={statusCat}
                                        score={indicator?.computed?.status_score}
                                        size={14}
                                      />
                                    )}
                                    <span
                                      className="indicator-trend-arrow"
                                      style={{ color: trendData.color }}
                                      title={
                                        indicator?.computed?.trend_status ||
                                        indicator?.computed?.trend ||
                                        'Progress not available'
                                      }
                                    >
                                      {trendData.arrow}
                                    </span>
                                  </div>
                                  <div className="indicator-info-tight">
                                    <span className="indicator-index">{indicator.acf?.index || ''}</span>
                                    <span className="indicator-title">
                                      {indicator.title_render || indicator.title || ''}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Download PDF Section */}
        <Row className="download-section-compact">
          <Col className="text-center">
            <Button
              variant="primary"
              size="lg"
              disabled={isGeneratingPDF}
              onClick={handleDownloadPDF}
              className="download-btn-compact"
            >
              <FileDownloadOutlined style={{ marginRight: 8 }} />
              {isGeneratingPDF ? 'Generating PDF...' : (isEU ? 'View and Download Kosovo EU SDG Profile' : 'View and Download Kosovo UN SDG Profile')}
            </Button>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SDGProfilePage;
